import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useFormikContext } from 'formik';
import flat from 'flat';
import MUITooltip from '@material-ui/core/Tooltip';

import BaseDialog from 'core/components/base-dialog/base-dialog';
import { Label } from 'core/components/label';
import { Tag } from 'core/components/tag';
import { useRouteHelper } from 'core/hooks/use-route-helper';
import warningIcon from 'core/assets/svg/ic-warning-green.svg';
import blockedIcon from 'core/assets/svg/ic-blocked-red.svg';
import goToIcon from 'core/assets/svg/ic-goto.svg';

import useStyles from './validation-errors-modal.styles';
import { enhanceFieldName, formatSegment } from './enhance-field-name';
// Data + classification constants live in the .constants file so this one stays render-focused.
import {
  REJECTIONS_ERROR_FIELD,
  REJECTIONS_SECTION_LABEL,
  REJECTIONS_NAV_ANCHOR,
  FARM_INSURES_NOTHING_FIELD,
  FARM_INSURES_NOTHING_SECTION_LABEL,
  MISSING_PRIMARY_HOMEOWNER_FIELD,
  MISSING_PRIMARY_HOMEOWNER_SECTION_LABEL,
  QUOTE_VALIDATION_UNAVAILABLE_FIELD,
  QUOTE_VALIDATION_UNAVAILABLE_SECTION_LABEL,
  SECTION_RULES,
  OTHER_ISSUES_LABEL,
  SECTION_NAV_TARGETS,
  MERGED_MORTGAGE_FIELD,
  MERGED_MORTGAGE_LOAN_NUMBER_FIELD,
  MERGED_MORTGAGE_MESSAGE,
  getSectionNavTarget,
  resolveSectionLabel,
  LIABILITY_LABEL,
  LOCATIONS_LABEL,
  POLICY_WIDE_ENDORSEMENTS_LABEL
} from './validation-errors-modal.constants';

// Re-exported so existing consumers/tests can keep importing these from the component module.
export {
  REJECTIONS_ERROR_FIELD,
  REJECTIONS_SECTION_LABEL,
  REJECTIONS_NAV_ANCHOR,
  FARM_INSURES_NOTHING_FIELD,
  FARM_INSURES_NOTHING_SECTION_LABEL,
  MISSING_PRIMARY_HOMEOWNER_FIELD,
  MISSING_PRIMARY_HOMEOWNER_SECTION_LABEL,
  QUOTE_VALIDATION_UNAVAILABLE_FIELD,
  QUOTE_VALIDATION_UNAVAILABLE_SECTION_LABEL,
  SECTION_RULES,
  OTHER_ISSUES_LABEL,
  SECTION_NAV_TARGETS,
  MERGED_MORTGAGE_FIELD,
  MERGED_MORTGAGE_LOAN_NUMBER_FIELD,
  MERGED_MORTGAGE_MESSAGE,
  getSectionNavTarget,
  resolveSectionLabel,
  LIABILITY_LABEL,
  LOCATIONS_LABEL,
  POLICY_WIDE_ENDORSEMENTS_LABEL
};

// Synthetic fields that render in the Blocking-issues section, never as a fixable card. Kept as a set
// so groupValidationErrors and the render path agree on what counts as a blocking (non-fixable) error.
const BLOCKING_ERROR_FIELDS = new Set([
  REJECTIONS_ERROR_FIELD,
  FARM_INSURES_NOTHING_FIELD,
  MISSING_PRIMARY_HOMEOWNER_FIELD,
  QUOTE_VALIDATION_UNAVAILABLE_FIELD
]);

// True for the escrow-mortgage lender fields the designer asked to collapse into a single modal row
// (`primaryMortgageDetail.mortgageHolderName` / `.mortgageHolderAddress[.*]`), but NOT the loan
// number (it keeps its own row). See MERGED_MORTGAGE_* in the constants file for the why.
const isMergeableMortgageField = (fieldName) =>
  (fieldName === MERGED_MORTGAGE_FIELD || fieldName.startsWith(`${MERGED_MORTGAGE_FIELD}.`)) &&
  fieldName !== MERGED_MORTGAGE_LOAN_NUMBER_FIELD;

const camelCaseToWordsWithSpaces = (str) => {
  return formatSegment(str.replace(/\./g, ' '));
};

const matchesPrefix = (pathSegments, prefixSegments) =>
  prefixSegments.length <= pathSegments.length &&
  prefixSegments.every((prefixSegment, i) => prefixSegment === '*' || prefixSegment === pathSegments[i]);

const classifyField = (fieldName) => {
  const pathSegments = fieldName.split('.');
  let match = null;
  SECTION_RULES.forEach((rule, order) => {
    rule.prefixes.forEach((prefix) => {
      const prefixSegments = prefix.split('.');
      if (matchesPrefix(pathSegments, prefixSegments) && (!match || prefixSegments.length > match.specificity)) {
        match = { label: rule.label, order, specificity: prefixSegments.length };
      }
    });
  });
  return match;
};

// Group flattened Formik errors into fixable-issue cards.
// Returns [{ section, errors: [{ fieldName, fieldLabel, message }] }] in SECTION_RULES order.
export const groupValidationErrors = (flatErrors, values) => {
  const sections = new Map();
  // The escrow-mortgage lender fields collapse into a single row; track whether we've already
  // emitted that merged row so the 2nd…nth lender error is dropped (loan number is excluded by
  // isMergeableMortgageField and so still gets its own row).
  let mergedMortgageEmitted = false;
  Object.keys(flatErrors).forEach((fieldName) => {
    if (BLOCKING_ERROR_FIELDS.has(fieldName)) {
      return;
    }
    const mergeMortgage = isMergeableMortgageField(fieldName);
    if (mergeMortgage && mergedMortgageEmitted) {
      return;
    }
    const message = mergeMortgage ? MERGED_MORTGAGE_MESSAGE : flatErrors[fieldName];
    const fieldLabel = fieldName.includes('farmLocations')
      ? enhanceFieldName(fieldName, values)
      : camelCaseToWordsWithSpaces(fieldName);
    if (!fieldLabel || !message) {
      return;
    }
    const match = classifyField(fieldName);
    // #32032: relabel the farm risk sections per farm product (Dwelling details / Risk coverages on
    // Home/Rental). The product lives on `policyType` in the customer-policy flow but on
    // `selectedOption` in the offer flow (policyType is empty there) — read whichever is present.
    const farmProductType = values?.policyType || values?.selectedOption;
    const section = resolveSectionLabel(match ? match.label : OTHER_ISSUES_LABEL, farmProductType);
    const order = match ? match.order : Number.POSITIVE_INFINITY;
    if (!sections.has(section)) {
      sections.set(section, { section, order, errors: [] });
    }
    sections.get(section).errors.push({ fieldName, fieldLabel, message });
    if (mergeMortgage) {
      mergedMortgageEmitted = true;
    }
  });
  return [...sections.values()].sort((a, b) => a.order - b.order).map(({ section, errors }) => ({ section, errors }));
};

// Recursively convert Formik error object to touched structure with boolean flags.
// Formik's setTouched expects { field: true }, not { field: "Error message" }.
export const setTouchedFromErrors = (errors) => {
  if (Array.isArray(errors)) {
    return errors.map((item) => (item && typeof item === 'object' ? setTouchedFromErrors(item) : true));
  }
  const touched = {};
  Object.keys(errors).forEach((key) => {
    touched[key] = typeof errors[key] === 'object' && errors[key] !== null ? setTouchedFromErrors(errors[key]) : true;
  });
  return touched;
};

export const useValidationErrorsModal = () => {
  const [showValidationErrorsModal, setShowValidationErrorsModal] = useState(false);
  return { showValidationErrorsModal, setShowValidationErrorsModal };
};

// Renders an error card. When `navTarget` is set (the section has a scroll anchor in this flow and
// the consumer supports go-to), the WHOLE card becomes the click target — a native <button> reset
// to look like the static card, wrapped in a "Go to <section>" tooltip — per the design
// (#30230 node 60179-107632). Otherwise it's a plain, non-interactive div. Shared by the Blocking
// "Offer rejections" card and the Fixable cards so their hover/click behavior stays identical.
const ErrorCard = ({ classes, section, navTarget, onGoToSection, className, children }) => {
  if (!navTarget) {
    return <div className={classNames(classes.errorCard, className)}>{children}</div>;
  }
  return (
    <MUITooltip
      title={`Go to ${section.toLowerCase()}`}
      placement="top"
      arrow
      classes={{ tooltip: classes.goToTooltip }}
    >
      <button
        type="button"
        aria-label={`Go to ${section}`}
        className={classNames(classes.errorCard, classes.clickableCard, className)}
        onClick={() => onGoToSection(navTarget)}
      >
        {children}
      </button>
    </MUITooltip>
  );
};

ErrorCard.propTypes = {
  classes: PropTypes.object.isRequired,
  section: PropTypes.string.isRequired,
  navTarget: PropTypes.object,
  onGoToSection: PropTypes.func,
  className: PropTypes.string,
  children: PropTypes.node.isRequired
};

ErrorCard.defaultProps = {
  navTarget: null,
  onGoToSection: undefined,
  className: undefined
};

const ValidationErrorsModal = ({ open, onClose, onGoToSection }) => {
  const classes = useStyles();
  const { errors, values } = useFormikContext();
  // Derive the flow from the route so consumers can't pass the wrong one. Offer edit, the UW form,
  // and offer checkout are all the offer flow; the customer policy route is the policy flow. Anything
  // else (e.g. farm sub-dialogs reached elsewhere) yields null → no go-to arrows.
  const { inPolicyRoute, inOfferEditRoute, inUWFormRoute, inOfferCheckoutRoute } = useRouteHelper();
  let flow = null;
  if (inOfferEditRoute || inUWFormRoute || inOfferCheckoutRoute) {
    flow = 'offer';
  } else if (inPolicyRoute) {
    flow = 'policy';
  }
  const flatErrors = flat(errors);
  // Blocking cards, in priority order. Each synthetic blocking field (offer rejections, farm
  // insures-nothing) renders its own card in the Blocking-issues section; they use distinct field
  // paths so one can never suppress the other (see BLOCKING_ERROR_FIELDS). The card is clickable when
  // its section has a scroll anchor in this flow (designer PR feedback, Jun 16) — same
  // null-when-unsupported rule as the fixable cards.
  const blockingCards = [
    { field: QUOTE_VALIDATION_UNAVAILABLE_FIELD, label: QUOTE_VALIDATION_UNAVAILABLE_SECTION_LABEL },
    { field: REJECTIONS_ERROR_FIELD, label: REJECTIONS_SECTION_LABEL },
    { field: FARM_INSURES_NOTHING_FIELD, label: FARM_INSURES_NOTHING_SECTION_LABEL },
    { field: MISSING_PRIMARY_HOMEOWNER_FIELD, label: MISSING_PRIMARY_HOMEOWNER_SECTION_LABEL }
  ]
    .map(({ field, label }) => ({
      field,
      label,
      message: flatErrors[field],
      navTarget: onGoToSection ? getSectionNavTarget(label, flow) : null
    }))
    .filter(({ message }) => !!message);
  const errorSections = groupValidationErrors(flatErrors, values);
  const errorCount = errorSections.reduce((count, { errors: sectionErrors }) => count + sectionErrors.length, 0);
  const showFixableSection = errorCount > 0 || blockingCards.length === 0;

  return (
    <BaseDialog open={open} onClose={onClose} className={classes.dialog}>
      <Label type="greenBig" className={classes.modalHeader}>
        Review and fix these issues
      </Label>
      {blockingCards.length > 0 && (
        <div className={classes.issuesSection}>
          <div className={classes.sectionHeadingRow}>
            <Label className={classes.sectionTitle}>Blocking issues</Label>
            <Tag className={classes.countChip}>{blockingCards.length}</Tag>
          </div>
          <Label className={classes.sectionSubtitle}>
            This offer cannot be purchased until these issues are resolved.
          </Label>
          {blockingCards.map(({ field, label, message, navTarget }) => (
            <ErrorCard
              key={field}
              classes={classes}
              section={label}
              navTarget={navTarget}
              onGoToSection={onGoToSection}
              className={classes.blockingCard}
            >
              <div className={classes.cardTitleRow}>
                <img src={blockedIcon} alt="" className={classes.blockedIcon} />
                <Label className={classNames(classes.cardTitle, classes.blockingCardTitle)}>{label}</Label>
                {navTarget && <img src={goToIcon} alt="" className={classes.goToArrow} />}
              </div>
              <div className={classes.errorItem}>
                <Label className={classes.errorMessage}>{message}</Label>
              </div>
            </ErrorCard>
          ))}
        </div>
      )}
      {showFixableSection && (
        <div className={classes.issuesSection}>
          <div className={classes.sectionHeadingRow}>
            <Label className={classes.sectionTitle}>Fixable issues</Label>
            {errorCount > 0 && <Tag className={classes.countChip}>{errorCount}</Tag>}
          </div>
          <Label className={classes.sectionSubtitle}>Update the information below to continue.</Label>
          {errorCount === 0 ? (
            <Label className={classes.errorMessage}>
              Some fields have validation errors. Please review the form for highlighted fields.
            </Label>
          ) : (
            <div className={classes.errorCards}>
              {errorSections.map(({ section, errors: sectionErrors }) => {
                // Per the design (#30230 node 60179-107632) the WHOLE card is the click target when
                // the section has a scroll anchor in this flow, with a "Go to <section>" tooltip on
                // hover. Cards without an anchor stay static (no arrow, not clickable, no tooltip).
                const navTarget = onGoToSection ? getSectionNavTarget(section, flow) : null;

                return (
                  <ErrorCard
                    key={section}
                    classes={classes}
                    section={section}
                    navTarget={navTarget}
                    onGoToSection={onGoToSection}
                  >
                    <div className={classes.cardTitleRow}>
                      <img src={warningIcon} alt="" className={classes.warningIcon} />
                      <Label className={classes.cardTitle}>{section}</Label>
                      {navTarget && <img src={goToIcon} alt="" className={classes.goToArrow} />}
                    </div>
                    {sectionErrors.map(({ fieldName, message }) => (
                      <div key={fieldName} className={classes.errorItem}>
                        <Label className={classes.errorMessage}>{message}</Label>
                      </div>
                    ))}
                  </ErrorCard>
                );
              })}
            </div>
          )}
        </div>
      )}
    </BaseDialog>
  );
};

ValidationErrorsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  // Called with the resolved nav target { anchor, tab? } when a card's go-to arrow is clicked.
  // Only wired up by consumers that support scroll-to-section; without it, no arrows render.
  // The flow (offer vs policy) is derived from the route, not passed in.
  onGoToSection: PropTypes.func
};

ValidationErrorsModal.defaultProps = {
  onGoToSection: undefined
};

export default ValidationErrorsModal;
